"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ClassicLoader from "@/components/common/classic-loader";
import { FormInput } from "@/components/form/form-input";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { AuthDivider } from "@/features/auth/components/elements/auth-divider";
import { AuthInfoBox } from "@/features/auth/components/elements/auth-info-box";
import { SocialSignInButtons } from "@/features/auth/components/elements/social-sign-in-buttons";
import {
  AUTH_PRIMARY_BUTTON,
  AUTH_SECONDARY_BUTTON,
} from "@/features/auth/lib/auth-button-styles";
import { AUTH_FORM_IDS } from "@/features/auth/lib/auth-step-config";
import type {
  OAuthProvider,
  PasswordStepVariant,
} from "@/features/auth/types/auth-flow.type";

const FORM_ID = AUTH_FORM_IDS.password;

const formSchema = z.object({
  password: z.string().min(1, {
    message: "auth.password.validators.required",
  }),
});

const LINK_CLASS =
  "text-sm text-text leading-[22px] underline underline-offset-4 hover:no-underline";

type PasswordStepProps = {
  email: string;
  variant: PasswordStepVariant;
  provider: OAuthProvider | null;
  isSubmitting: boolean;
  /** Translation key of a credentials error returned by Clerk. */
  serverError: string | null;
  /** Hides the inline submit so the host can render it in a bottom bar. */
  submitInFooter?: boolean;
  onSubmit: (password: string) => void;
  onChangeEmail: () => void;
  onForgotPassword: () => void;
  onProviderSelect: (provider: OAuthProvider) => void;
};

export const PasswordStep = ({
  email,
  variant,
  provider,
  isSubmitting,
  serverError,
  submitInFooter = false,
  onSubmit,
  onChangeEmail,
  onForgotPassword,
  onProviderSelect,
}: PasswordStepProps) => {
  const t = useTranslations("auth");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { password: "" },
  });

  const { setError } = form;

  useEffect(() => {
    if (serverError) {
      setError("password", { message: serverError });
    }
  }, [serverError, setError]);

  const isUnlinked = variant === "unlinked-account";
  const providerName = provider ? t(`social.providerName.${provider}`) : "";
  const visibilityLabel = isPasswordVisible
    ? t("password.hide")
    : t("password.show");

  return (
    <div className="flex flex-col gap-6">
      {isUnlinked ? (
        <AuthInfoBox
          description={t("unlinkedAccount.description", {
            email,
            provider: providerName,
          })}
          title={t("unlinkedAccount.title")}
        />
      ) : (
        <AuthInfoBox
          action={
            <button
              className={LINK_CLASS}
              onClick={onChangeEmail}
              type="button"
            >
              {t("password.changeEmail")}
            </button>
          }
          description={email}
          title={t("password.signingInTo")}
          truncateDescription
        />
      )}

      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          id={FORM_ID}
          onSubmit={form.handleSubmit((values) => onSubmit(values.password))}
        >
          <div className="flex flex-col gap-3">
            <FormInput
              autoComplete="current-password"
              fieldLabel={t("password.label")}
              fieldName="password"
              form={form}
              onTrailingIconClick={() =>
                setIsPasswordVisible((isVisible) => !isVisible)
              }
              required
              trailingIcon={
                <span className="font-medium text-sm text-text leading-[22px]">
                  {visibilityLabel}
                </span>
              }
              trailingIconLabel={visibilityLabel}
              type={isPasswordVisible ? "text" : "password"}
            />
            <button
              className={`${LINK_CLASS} self-end md:self-start`}
              onClick={onForgotPassword}
              type="button"
            >
              {t("password.forgotPassword")}
            </button>
          </div>

          {!submitInFooter && (
            <Button
              className={AUTH_PRIMARY_BUTTON}
              disabled={isSubmitting}
              type="submit"
              data-pw="submit-sign-in-button"
            >
              {t("password.submit")}
              {isSubmitting && <ClassicLoader className="ml-2" />}
            </Button>
          )}
        </form>
      </Form>

      {isUnlinked ? (
        <Button
          className={AUTH_SECONDARY_BUTTON}
          onClick={onChangeEmail}
          type="button"
          variant="outline"
        >
          {t("linkedAccount.backToSignIn")}
        </Button>
      ) : (
        // Social sign-in stays a desktop affordance here: the mobile drawer
        // keeps the password step to the credentials only.
        <div className="hidden flex-col gap-6 md:flex">
          <AuthDivider />
          <SocialSignInButtons
            disabled={isSubmitting}
            onSelect={onProviderSelect}
          />
        </div>
      )}
    </div>
  );
};
